import StorageService from '@/services/storage.service'
import storageKeys from '@/constants/storageKeys'
import { defaultFilters, defaultFiltersPlayer } from '@/models/PlayersReports/helpers'
import reportsRepository from '@/repository/generatedRepository/reportsRepository'
import playersRepository from '@/repository/generatedRepository/playersRepository'
import TableOptions from '@/models/TableOptions'
import { getSortedItems, omitEmptyDeepFilters, prepareFilters } from '@/helpers/filters'
import playerReportsRepository from '@/repository/generatedRepository/playerReportsRepository'
const { saveAs } = require('file-saver')

const state = {
  listApi: [],
  reportsList: [],
  filters: StorageService.get(storageKeys.PLAYERS_REPORTS_FILTERS) || { ...defaultFilters() },
  filtersApi: StorageService.get(storageKeys.PLAYERS_REPORTS_API_FILTERS) || { ...defaultFilters() },
  filtersPlayer: StorageService.get(storageKeys.PLAYER_REPORT_FILTERS) || { ...defaultFiltersPlayer() },
  tableOptionsApi: TableOptions.defaultTableOptions(),
  tableOptionsPlayer: TableOptions.defaultTableOptions(),
}

const getters = {
  filters: state => state.filters,
  filtersApi: state => state.filtersApi,
  filtersPlayer: state => state.filtersPlayer,
  tableOptionsApi: state => state.tableOptionsApi,
  tableOptionsPlayer: state => state.tableOptionsPlayer,
  listApi: state => state.listApi,
  reportsList: state => state.reportsList,
  reportsListPrepared: state =>
    getSortedItems(state.reportsList, {
      sortBy: state.filtersPlayer.sortBy,
      sortDesc: state.filtersPlayer.sortDesc,
    }),
  isOpenFilterPanel: state => state.filters.clientOptions && state.filters.clientOptions.filterPanelExpanded,
  isOpenFilterPanelPlayer: state =>
    state.filtersPlayer.clientOptions && state.filtersPlayer.clientOptions.filterPanelExpanded,
}

const actions = {
  async loadDataApi(context, payload) {
    const { signal, ...filters } = payload
    context.commit('setApiFilters', filters)
    StorageService.set(storageKeys.PLAYERS_REPORTS_API_FILTERS, filters)

    const appliedFilters = omitEmptyDeepFilters(filters.filter)
    const appliedCfFilters = omitEmptyDeepFilters(filters.cfFilter)

    const preparedFilters = prepareFilters({
      ...filters,
      filter: {
        ...appliedFilters,
        type: 'api',
      },
      cfFilter: appliedCfFilters,
      signal,
      isFilter: 1,
    })
    const { data } = await reportsRepository.list(preparedFilters)

    context.commit('setApiList', { data, filters })

    return { products: data.items, pagination: data.pagination }
  },
  async syncApi(context, payload) {
    const { siteIds, ...dates } = payload
    const searchParams = new URLSearchParams(dates)
    if (Array.isArray(siteIds)) siteIds.forEach(siteId => searchParams.append('siteIds[]', siteId))
    return await playersRepository.synchronize(searchParams.toString())
  },
  syncXlsx(context, { file, date, advertiser, siteIds }) {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('date', date)
    formData.append('advertiser', advertiser)
    if (Array.isArray(siteIds)) siteIds.forEach(siteId => formData.append('siteIds[]', siteId))
    return playerReportsRepository.xlsxImport(formData, { skip403Redirect: true })
  },
  async loadItem(context, id) {
    const { data } = await reportsRepository.get(id)
    return data
  },
  async loadReportPlayers(context, filters) {
    const { id, signal, ...prepFilters } = filters
    context.commit('setFiltersPlayer', prepFilters)
    StorageService.set(storageKeys.PLAYER_REPORT_FILTERS, prepFilters)

    const appliedFilters = omitEmptyDeepFilters(prepFilters.filter)
    const appliedCfFilters = omitEmptyDeepFilters(prepFilters.cfFilter)

    const preparedFilters = prepareFilters({
      ...prepFilters,
      filter: {
        ...appliedFilters,
        report: {
          id,
        },
      },
      cfFilter: appliedCfFilters,
      signal,
    })

    const { data } = await playerReportsRepository.list(preparedFilters)
    data.items.forEach(el => (el.id = el.player.id))

    context.commit('setReportsList', {
      data,
      filters: prepFilters,
    })
  },
  createItem(context, payload) {
    return reportsRepository.post(payload)
  },
  updateItem(context, payload) {
    const { id, ...data } = payload
    return reportsRepository.put(id, data)
  },
  deleteItem(context, payload) {
    return reportsRepository.delete(payload)
  },
  async retryReport({ commit, dispatch, state }, id) {
    commit('updateApiListItem', { id, lastRetryStatus: 'pending' })
    try {
      await reportsRepository.retry(id)
      return await dispatch('loadDataApi', state.filtersApi)
    } catch (e) {
      await dispatch('loadDataApi', state.filtersApi)
      throw e
    }
  },
  async loadRetryHistory(context, id) {
    const { data } = await reportsRepository.retryHistory(id)
    return data
  },
  filtersStorageReset() {
    StorageService.remove(storageKeys.PLAYERS_REPORTS_FILTERS)
  },
  async getReportFile(context, { fileName = 'report.txt', ...payload }) {
    const response = await reportsRepository.getFile(payload)
    const blob = new Blob([response.data], { type: response.headers['content-type'] })
    saveAs(blob, fileName)
    return response
  },
  async getParseData(context, { item, field, reportId }) {
    const response = await reportsRepository.getParseData({ id: item.id, field })
    const blob = new Blob([response.data], { type: response.headers['content-type'] })
    saveAs(blob, `${field}_${reportId}-${item.attemptNumber}`)
    return response
  },
  resetSearchFilter({ commit }) {
    commit('clearSearch')
  },
  updateFiltersClientOptions({ commit, getters }, clientOptions) {
    const updatedFilters = {
      ...getters.filters,
      clientOptions,
    }
    commit('setFilters', updatedFilters)
    StorageService.set(storageKeys.PLAYERS_REPORTS_FILTERS, updatedFilters)
  },
  updateFiltersPlayerClientOptions({ commit, getters }, clientOptions) {
    const updatedFilters = {
      ...getters.filtersPlayer,
      clientOptions,
    }
    commit('setFiltersPlayer', updatedFilters)
    StorageService.set(storageKeys.PLAYER_REPORT_FILTERS, updatedFilters)
  },
}

const mutations = {
  setApiList(state, { data, filters }) {
    state.listApi = [...data.items]
    state.tableOptionsApi = new TableOptions(data.pagination, filters)
  },
  setReportsList(state, { data, filters }) {
    state.reportsList = [...data.items]
    state.tableOptionsPlayer = new TableOptions(data.pagination, filters)
  },
  setFilters(state, filters) {
    state.filters = { ...filters }
  },
  setApiFilters(state, filters) {
    state.filtersApi = { ...filters }
  },
  setFiltersPlayer(state, filters) {
    state.filtersPlayer = { ...filters }
  },
  clearSearch(state) {
    state.tableOptionsApi.search = ''
  },
  updateApiListItem(state, updatedItem) {
    const index = state.listApi.findIndex(item => item.id === updatedItem.id)
    if (index !== -1) {
      state.listApi.splice(index, 1, { ...state.listApi[index], ...updatedItem })
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
